import {ProjectColors} from "../../../Enums/Core/projectColors";
import {Button, Col, Row, Spinner} from "react-bootstrap";
import BarChart from "../../BarChart/BarChart";
import React, {useEffect, useState} from "react";
import TimeScaleFilter from "../TimeScaleFilter/TimeScaleFilter";
import DrillDownModal from "../DrillDownModal/DrillDownModal";
import {
    DAYS, EXCEL_TITLE_WATER_CONSUMPTION, HOURS, MONTHS, WATER_CONSUMPTION_NAME, WEEKS
} from "../../../Constants/constants";
import {apolloClientReports} from "../../../GraphQL/config";
import {QUERY_GET_WATER_CONSUMPTION} from "../../../GraphQL/ReportGenerator/queries";
import {
    getDailyOffsetEndDate,
    getDailyOffsetStartDate,
    getEndDate,
    getMonthlyOffsetEndDate,
    getMonthlyStartDate,
    getMonthlyOffsetStartDate,
    getResolutionName,
    getWeeklyOffsetEndDate,
    getWeeklyOffsetStartDate,
    pushCsvDates,
    setCsvHeaders,
    sortResponseData,
    buildCategoriesData,
    getPointResolution,
    setDisabledOffsetButtons
} from "../Utils/utils";
import WaterConsumptionHighResolution from "./WaterConsumptionHighResolution";
import {BsFillSkipEndFill, BsFillSkipStartFill} from "react-icons/bs";
import convert, {DEFAULT_VOLUME_UNIT} from "../MeasurementText/MeasurementTextConverter";

export default function WaterConsumption(props) {
    const [selectedDate, setSelectedDate] = useState(props.selectedDate || null);
    const [isLoading, setIsLoading] = useState(true);
    const [chartData, setChartData] = useState([]);
    const [modalShow, setModalShow] = React.useState(false);
    const [resolution, setResolution] = useState(MONTHS);
    const [volumeUnit, setVolumeUnit] = useState(DEFAULT_VOLUME_UNIT);
    const [offset, setOffset] = useState(0);
    const [startDate, setStartDate] = useState(getMonthlyOffsetStartDate(offset));
    const [endDate, setEndDate] = useState(getMonthlyOffsetEndDate(offset));
    const [categories, setCategories] = useState();
    const [barIndex, setBarIndex] = useState(MONTHS);
    const [csvData, setCsvData] = useState([]);
    const [leftArrowDisabled, setLeftArrowDisabled] = useState(false);
    const [rightArrowDisabled, setRightArrowDisabled] = useState(true);

    const getWaterConsumptionDataFromDB = async () => {
        await apolloClientReports.query({
            query: QUERY_GET_WATER_CONSUMPTION, variables: {
                resolution: getResolutionName(resolution),
                from: startDate.toISOString().split('T')[0],
                to: endDate.toISOString().split('T')[0]
            }
        }).then(res => {
            let response = res?.data?.waterConsumptionHistogram;
            if (!response) {
                console.error(`Data from WaterConsumptionData is empty`);
            } else {
                setVolumeUnit(convert(response.measureUnit.volumeUnit));
                let responseData = sortResponseData(response?.data);
                setChartData([{
                    name: WATER_CONSUMPTION_NAME,
                    data: responseData.map((res) => res.value),
                    color: ProjectColors.Aqua,
                    showInLegend: false
                }]);
                setDisabledOffsetButtons(responseData, offset, setRightArrowDisabled, setLeftArrowDisabled, startDate, resolution);
                setCategories(buildCategoriesData(responseData, resolution));
                setCsvData(buildCsvData(responseData));
                setIsLoading(false);
            }
        })
    }

    const buildCsvData = (responseData) => {
        let csvData = [];
        let headers = setCsvHeaders(resolution);
        headers.push(`Water consumption (${volumeUnit})`);
        csvData.push(headers);
        responseData.forEach((entity, index) => {
            let row = [];
            row.push(index + 1);
            row = pushCsvDates(resolution, entity.date, row);
            row.push(entity.value);
            csvData.push(row);
        });

        return csvData;
    }

    useEffect(() => {
        setIsLoading(true);
        getWaterConsumptionDataFromDB();
    }, [startDate, endDate]);

    useEffect(() => {
        switch (resolution) {
            case MONTHS:
                setStartDate(getMonthlyOffsetStartDate(offset));
                setEndDate(getMonthlyOffsetEndDate(offset));
                break;
            case WEEKS:
                setStartDate((getWeeklyOffsetStartDate(offset)));
                setEndDate((getWeeklyOffsetEndDate(offset)));
                break;
            case DAYS:
                setStartDate(getDailyOffsetStartDate(offset));
                setEndDate(getDailyOffsetEndDate(offset));
                break;
        }
    }, [offset]);

    useEffect(() => {
        setIsLoading(true);
        if (offset !== 0) setOffset(0);
        let now = new Date();
        switch (resolution) {
            case HOURS:
                setStartDate(now);
                setEndDate(getEndDate(props.selectedDate, resolution));
                break;
            case DAYS:
                setStartDate(getDailyOffsetStartDate(offset));
                setEndDate(getDailyOffsetEndDate(offset));
                break;
            case WEEKS:
                setStartDate((getWeeklyOffsetStartDate(offset)));
                setEndDate((getWeeklyOffsetEndDate(offset)));
                break;
            default:
                setStartDate(getMonthlyStartDate());
                setEndDate(now);
                break;
        }
    }, [resolution]);

    return (<>
        {!chartData && !csvData && <Spinner animation="border" variant="primary"/>}
        {chartData && csvData && <div>
            <Row>
                <Col md={6}>
                    <p className={'card-title-main bold-title'}>{props.title}</p>
                </Col>
                <Col md={3}>
                    <TimeScaleFilter
                        setOffSet={setOffset}
                        hasExportCsv
                        setScale={setResolution}
                        csvData={csvData}
                        title={EXCEL_TITLE_WATER_CONSUMPTION}/>
                </Col>
            </Row>
            {isLoading && <Spinner animation="border" variant="primary"/>}
            {!isLoading && <>
                <Row className={'cardHeaderChart'}>
                    <Col md={1}><Button
                        className={'offset_button'}
                        disabled={leftArrowDisabled}
                        onClick={() => setOffset(offset - 1)}> <BsFillSkipStartFill size={30} color={'#686B73'}/>
                    </Button>
                    </Col>
                    <Col md={10}>
                        <BarChart
                            volumeUnit={volumeUnit}
                            categories={categories}
                            data={chartData}
                            onClick={(event) => {
                                getPointResolution(event, resolution, setSelectedDate, setBarIndex, setModalShow)
                            }}
                        />
                    </Col>
                    <Col md={1}><Button
                        className={'offset_button'}
                        disabled={rightArrowDisabled}
                        onClick={() => setOffset(offset + 1)}> <BsFillSkipEndFill size={30} color={'#686B73'}/>
                    </Button>
                    </Col>
                </Row>
                {selectedDate && <DrillDownModal show={modalShow}
                                                 onHide={() => setModalShow(false)}
                                                 type={WaterConsumptionHighResolution}
                                                 resolution={resolution}
                                                 barIndex={barIndex}
                                                 title={props.title}
                                                 selectedDate={selectedDate}/>}
            </>}
        </div>}
    </>)
}
